/**
 * @pit/sim — stats + action gating (aura-aware reads).
 *
 * MANDATORY ACCESS PATTERN. wp3-sim-core.md §"Stat access & action gating":
 * NO code outside this module reads `champion.stats.X` directly, and NO
 * code outside this module derives liveness from `hp > 0`. Auras (#30)
 * modify stats via `statMod` primitives and gate actions via
 * `controlFlag` primitives — funnelling every read through here keeps
 * every ability composition-based rather than mechanic-per-use-site.
 *
 * Reviewer discipline: any use site of `champion.stats.<key>` outside this
 * file, or any `entity.hp > 0` liveness check, is a bug. A CI grep guard
 * lives in `tooling/lint-meta/` (see WP0 wiring). Add one for future WPs.
 *
 * NB (aura wiring, #30): only `periodic` primitives are wired for
 * behavior in the tick pipeline (Corruption). `statMod` and `controlFlag`
 * READS are implemented here because the accessor signatures need
 * `tick` anyway — the read path is future-proof. Absorb / immunity /
 * trigger primitives ship as inert exhaustive-switch arms
 * (reserved-arm pattern).
 */

import { idiv } from "@pit/math";
import type { Champion, Tick, WallBlockedTeams } from "@pit/protocol";
import type { ContentBundle } from "@pit/content";
import { isActive, isSuppressed } from "./auras.js";
import { NO_PHASING } from "./collision.js";
import type { Entity, PhasingFlags } from "./types.js";

/**
 * Stat keys currently readable through `getStat`. The set is intentionally
 * small — expand only when a new stat lands in `Champion.stats` (schema
 * change in WP1) and never carry raw stat reads at use sites.
 *
 * #326: `damageTakenBp`/`damageDealtBp` are BASE-LESS — no `Champion.stats`
 * field backs them (see `readBase`, which returns 0 for both). They exist
 * purely so an aura's `statMod` primitive can carry a signed "percent more/
 * less damage" delta, read by the `damage` effect arm.
 *
 * #548: `resourceGainBp` is the same shape, a third BASE-LESS key — it
 * exists purely so an aura's `statMod` primitive can carry a signed
 * "percent more/less resource gained" delta, read by the `gainResource`
 * effect arm (mana/rage/energy generation from ANY source: `trigger`,
 * `periodic`, or a plain ability/weapon effects entry).
 */
export type StatKey =
  | "maxHp"
  | "moveSpeed"
  | "radius"
  | "weight"
  | "armor"
  | "damageTakenBp"
  | "damageDealtBp"
  | "resourceGainBp";

/**
 * A magnitude stat (base ≥ 0, never legitimately negative) is floored at 0
 * by `getStat`. The #326/#548 bp-modifier stats are the exception: they are a
 * signed delta around a 0 ("no modifier") baseline with no base value, so a
 * stack of damage-taken (or resource-gain) reductions must be allowed to go
 * negative — the `damage`/`gainResource` arm alone clamps the resulting
 * PRACTICAL multiplier at 0.
 */
function isMagnitudeStat(key: StatKey): boolean {
  return key !== "damageTakenBp" && key !== "damageDealtBp" && key !== "resourceGainBp";
}

/**
 * Read a champion stat for an entity, applying active aura `statMod`
 * primitives on top of the base template value.
 *
 * Composition rule (deterministic, integer-only):
 *   1. Start with `base = champion.stats[key]` (0 for the #326 base-less
 *      bp-modifier keys — see `readBase`).
 *   2. Sum the `add` contributions from every active statMod primitive
 *      on this entity whose `stat === key` — this is a flat integer
 *      offset (post-additive). #326: each contribution is scaled by the
 *      aura instance's `stacks` count (a `stacking: { kind: "stack" }` def
 *      with a `statMod` primitive composes correctly — 5 stacks of a
 *      per-stack `add: -2000` sums to -10000 through ONE instance's stacks
 *      count, not 5 separate instances). `stacks` is always 1 for every
 *      `refresh`/`independentPerSource`/`independentInstances` aura (the
 *      only stacking modes any shipped/golden content uses today), so this
 *      is behaviorally neutral (×1) for all existing content.
 *   3. Sum the `addBp` contributions (basis points, signed, integer),
 *      likewise scaled by `stacks`. Applied via `idiv(base * sumBp, 10000)`
 *      — floored integer scale of the BASE (not the post-additive value) so
 *      ordering doesn't matter for the same-key case and every peer computes
 *      byte-identical values. For a base-less #326 key `base === 0`, so
 *      `addBp` always contributes 0 — a content author must use `add`
 *      instead (crossValidate rejects `addBp` on these two keys as a
 *      footgun-by-silent-no-op).
 *   4. Result = base + addSum + idiv(base * bpSum, 10000). A magnitude stat
 *      (maxHp/moveSpeed/radius/weight/armor) clamps ≥ 0; the #326 bp-modifier
 *      keys are left UNCLAMPED (signed delta, see `isMagnitudeStat`).
 *
 * Auras are consulted only when the entity has any; `bundle` is optional
 * to keep the accessor callable from tests + goldens that predate the
 * aura registry (an entity with no auras never triggers a bundle read).
 * `tick` is required for expiry / suppression derivation.
 */
export function getStat(
  entity: Entity,
  champion: Champion,
  key: StatKey,
  tick: Tick,
  bundle?: ContentBundle,
): number {
  // Sanity: caller must have looked up the right champion for this entity.
  // Cheap dev-time check; production is a no-op via simAssert semantics.
  if (champion.id !== entity.championId) {
    throw new Error(
      "getStat: champion.id (" +
        champion.id +
        ") != entity.championId (" +
        entity.championId +
        ")",
    );
  }
  const base = readBase(champion, key);
  if (entity.auras.length === 0 || bundle === undefined) return base;

  let addSum = 0;
  let bpSum = 0;
  for (const aura of entity.auras) {
    if (!isActive(entity, aura, tick, bundle)) continue;
    // #142 D2: the per-instance transient `statMod` (the old def-less `slow`)
    // was removed — a slow is now an ordinary def-driven aura, so its moveSpeed
    // reduction lives in `def.primitives` and flows through the `statMod` arm
    // below like every other stat mod. One code path, one composition.
    const def = bundle.auras.get(aura.def);
    /* c8 ignore next -- content invariant: applyAura validated the def. */
    if (def === undefined) continue;
    for (const prim of def.primitives) {
      // Exhaustive over the primitive union — the reserved arms
      // (absorbPool / immunity / periodic / trigger / reflectProjectile /
      // reflectEffect) contribute nothing to a stat READ. Keeping it exhaustive
      // means the compiler catches a missing branch when a new primitive is
      // added.
      switch (prim.kind) {
        case "statMod": {
          if (prim.stat !== key) break;
          // #326: scale by this INSTANCE's stack count so a `stacking: {
          // kind: "stack" }` def composes correctly through the single
          // instance `stack` stacking keeps (see auras.ts `applyAuraInstance`
          // — stacking does NOT mint N instances, it increments ONE
          // instance's `stacks`). `stacks` is always 1 for every
          // refresh/independentPerSource/independentInstances aura (the only
          // stacking modes any shipped/golden content uses), so this is a
          // ×1 no-op for all existing content.
          if (prim.op === "add") {
            addSum += prim.value * aura.stacks;
          } else {
            // op === "addBp"
            bpSum += prim.value * aura.stacks;
          }
          break;
        }
        case "controlFlag":
        case "absorbPool":
        case "immunity":
        case "periodic":
        case "trigger":
        case "reflectProjectile":
        case "reflectEffect":
        // #147: forced movement drives POSITION (via the step.ts movement
        // authority), never a stat read — inert here, like controlFlag.
        case "forcedMovement":
        // #158: blocksLoS drives LINE-OF-SIGHT (via `los.ts`), never a stat
        // read — inert here, like controlFlag.
        case "blocksLoS":
        // #199: phasing drives COLLISION/OVERLAP (via `collision.ts` + the
        // movement/projectile/invariant paths), never a stat read — inert here,
        // like controlFlag / blocksLoS.
        case "phasing":
        // #669: nextCastInstant drives CAST-TIME OVERRIDE (via `cast.ts`'s
        // `evaluateCastStart`), never a stat read — inert here, like
        // controlFlag / blocksLoS / phasing.
        case "nextCastInstant":
          break;
      }
    }
  }

  const scaled = bpSum === 0 ? 0 : idiv(base * bpSum, 10000);
  const total = base + addSum + scaled;
  return isMagnitudeStat(key) && total < 0 ? 0 : total;
}

/** Non-aura base read — hoisted so getStat's aura loop reads it once. */
function readBase(champion: Champion, key: StatKey): number {
  switch (key) {
    case "maxHp":
      return champion.stats.maxHp;
    case "moveSpeed":
      return champion.stats.moveSpeed;
    case "radius":
      return champion.stats.radius;
    case "weight":
      return champion.stats.weight;
    // #326/#548: base-less — these exist ONLY as an aura statMod target,
    // never a champion.stats field. 0 = "no modifier", the identity value
    // for a bp delta (mirrors a 0 armor champion being fully
    // mitigation-inert).
    case "damageTakenBp":
    case "damageDealtBp":
    case "resourceGainBp":
      return 0;
    case "armor":
      // #121: armor is optional on the champion stats schema (absent ⇒ 0, no
      // mitigation). The single nullish read lives HERE, in the mandatory
      // stat-access module — every effective-armor read funnels through
      // `getStat(..., "armor", ...)`, which composes aura statMods on top and
      // floors the result at 0.
      return champion.stats.armor ?? 0;
  }
}

/**
 * Can this entity take a controllable action (cast/attack)?
 *
 * Base: alive (`hp > 0`). Aura layer (#30): FALSE if any active
 * `controlFlag` primitive on the entity sets a disabling flag \u2014 the
 * relevant flags for a generic "canAct" check are `cast` and `attack`.
 * Movement is gated by `canMove` (finer-grained), and `targetable` is
 * a defender-side flag, so neither triggers this predicate.
 *
 * `bundle` is optional so tests / callers with an aura-free entity can
 * skip it; a non-empty auras array short-circuits to `true` only when
 * the bundle is available AND no controlFlag disables an action.
 * (`Corruption` uses no controlFlag \u2014 always true today.)
 */
export function canAct(
  entity: Entity,
  tick: Tick,
  bundle?: ContentBundle,
): boolean {
  if (entity.hp <= 0) return false;
  if (entity.auras.length === 0 || bundle === undefined) return true;
  for (const aura of entity.auras) {
    if (!isActive(entity, aura, tick, bundle)) continue;
    const def = bundle.auras.get(aura.def);
    if (def === undefined) continue;
    for (const prim of def.primitives) {
      if (prim.kind !== "controlFlag") continue;
      if (prim.flag === "cast" || prim.flag === "attack") return false;
    }
  }
  return true;
}

/**
 * Can this entity move this tick?
 *
 * Base: alive. Aura layer: FALSE if any active `controlFlag` primitive
 * sets `flag === "move"`. This is the movement-side twin of `canAct`,
 * separated so a "silence" that blocks casting doesn't also root the
 * entity (and vice versa \u2014 a root doesn't lock out casts).
 *
 * `bundle` is optional for the same reason as `canAct`; aura-free
 * entities short-circuit before any content read.
 */
export function canMove(
  entity: Entity,
  tick: Tick,
  bundle?: ContentBundle,
): boolean {
  if (entity.hp <= 0) return false;
  if (entity.auras.length === 0 || bundle === undefined) return true;
  for (const aura of entity.auras) {
    if (!isActive(entity, aura, tick, bundle)) continue;
    const def = bundle.auras.get(aura.def);
    if (def === undefined) continue;
    for (const prim of def.primitives) {
      if (prim.kind !== "controlFlag") continue;
      if (prim.flag === "move") return false;
    }
  }
  return true;
}

/**
 * Can this entity ATTACK this tick (#93 auto-attack gate)?
 *
 * Base: alive. Aura layer: FALSE if any active `controlFlag` primitive sets
 * `flag === "attack"`. The attack-only twin of `canMove`/`canAct`, separated so
 * a SILENCE (a `cast` controlFlag) does NOT stop auto-attacks while a STUN /
 * DISARM (an `attack` controlFlag) does — the auto is suppressed exactly when
 * the entity is attack-disabled. `canAct` returns false for EITHER cast or
 * attack, so it is too broad for the auto gate (a silence must not suppress an
 * auto); hence this dedicated predicate.
 *
 * `bundle` is optional for the same reason as `canMove`; aura-free entities
 * short-circuit before any content read.
 */
export function canAttack(
  entity: Entity,
  tick: Tick,
  bundle?: ContentBundle,
): boolean {
  if (entity.hp <= 0) return false;
  if (entity.auras.length === 0 || bundle === undefined) return true;
  for (const aura of entity.auras) {
    if (!isActive(entity, aura, tick, bundle)) continue;
    const def = bundle.auras.get(aura.def);
    if (def === undefined) continue;
    for (const prim of def.primitives) {
      if (prim.kind !== "controlFlag") continue;
      if (prim.flag === "attack") return false;
    }
  }
  return true;
}

/**
 * #158: the effective LINE-OF-SIGHT team gate for this entity's body, or `null`
 * if it has NO active `blocksLoS` aura (the common case). When non-null, the
 * entity's body occludes sight for viewers selected by the returned selector
 * (relative to THIS entity's team): `enemies` (the default — the big guy blocks
 * enemy sight), `allies`, or `all`.
 *
 * COMBINES multiple active `blocksLoS` primitives ORDER-INDEPENDENTLY so the
 * result never depends on aura array order (a determinism hazard): if any
 * primitive blocks enemies AND any blocks allies, the effective gate is `all`.
 * The twin of `canMove`/`canAttack` — a pure read of hashed aura state, no
 * mutation, no float. `bundle` is required to resolve the def primitives; an
 * aura-free entity short-circuits to `null` before any content read.
 */
export function losBlockTeams(
  entity: Entity,
  tick: Tick,
  bundle: ContentBundle,
): WallBlockedTeams | null {
  if (entity.auras.length === 0) return null;
  let blocksEnemies = false;
  let blocksAllies = false;
  let any = false;
  for (const aura of entity.auras) {
    if (!isActive(entity, aura, tick, bundle)) continue;
    const def = bundle.auras.get(aura.def);
    if (def === undefined) continue;
    for (const prim of def.primitives) {
      if (prim.kind !== "blocksLoS") continue;
      any = true;
      switch (prim.teams) {
        case "all":
          blocksEnemies = true;
          blocksAllies = true;
          break;
        case "enemies":
          blocksEnemies = true;
          break;
        case "allies":
          blocksAllies = true;
          break;
      }
    }
  }
  if (!any) return null;
  if (blocksEnemies && blocksAllies) return "all";
  return blocksEnemies ? "enemies" : "allies";
}

/**
 * #199: the effective pass-through PHASING state of this entity this tick — the
 * OR of every ACTIVE `phasing` aura primitive's four toggles. The collision twin
 * of `losBlockTeams`: a pure read of hashed aura state (no mutation, no float),
 * resolved from the bundle each query (a `phasing` primitive carries no
 * per-instance state). Returns the shared `NO_PHASING` constant when the entity
 * has no active phasing aura (the common case), so callers can skip cheaply.
 *
 * `inclusiveExpiry` (default false) selects the expiry boundary:
 *   - false (the MECHANIC — movement / collision / projectile): STRICT
 *     `tick < expiresAtTick` (the standard `isActive`). A holder stops phasing
 *     the tick its aura goes inactive.
 *   - true (the INVARIANT — I5/I6): treats the expiry tick as STILL phasing
 *     (`tick <= expiresAtTick`). This covers the ONE transitional tick where a
 *     phasing aura is attached-but-strict-inactive (it was active last tick,
 *     `advanceAuras` removes it THIS tick): the overlap it permitted still
 *     exists in the returned state, and is cleared (push-apart) the same tick
 *     the aura is removed. Suppression is still honored in both modes.
 */
export function phasingFlags(
  entity: Entity,
  tick: Tick,
  bundle: ContentBundle,
  inclusiveExpiry = false,
): PhasingFlags {
  if (entity.auras.length === 0) return NO_PHASING;
  let projectiles = false;
  let entities = false;
  let pillars = false;
  let walls = false;
  let any = false;
  for (const aura of entity.auras) {
    const activeNow = inclusiveExpiry
      ? (aura.expiresAtTick === null || tick <= aura.expiresAtTick) &&
        !isSuppressed(entity, aura, tick, bundle)
      : isActive(entity, aura, tick, bundle);
    if (!activeNow) continue;
    const def = bundle.auras.get(aura.def);
    if (def === undefined) continue;
    for (const prim of def.primitives) {
      if (prim.kind !== "phasing") continue;
      any = true;
      if (prim.projectiles) projectiles = true;
      if (prim.entities) entities = true;
      if (prim.pillars) pillars = true;
      if (prim.walls) walls = true;
    }
  }
  if (!any) return NO_PHASING;
  return { projectiles, entities, pillars, walls };
}

/**
 * Is this entity alive? Alias for readability at collision/target sites
 * where "canAct" reads oddly (e.g. "corpses don't block movement").
 *
 * Kept as its own name so semantic tenders can be introduced later without
 * churning call sites (e.g. an "ethereal" entity could be alive-but-non-
 * blocking). Auras do NOT affect liveness \u2014 `hp` is the single source of
 * truth (I2 invariant).
 */
export function isAlive(entity: Entity): boolean {
  return entity.hp > 0;
}
